<template>
  <AppHeaderMobileButton
    v-for="item in navItems"
    :key="item.path"
    :to="item.path"
    :icon="item.icon"
    :text="item.text"
    :is-active="item.isActive ?? isActive(item.path ?? '', Boolean(item.exact))"
    :show-badge="item.showBadge"
   
    @click="item.onClick ? item.onClick($event) : handleCloseMoreMenu(item.path)"
  />

  <AtomTransition variant="fade">
    <div
      v-if="moreMenuVisible"
      class="absolute bottom-full left-0 w-full h-[calc(100dvh-var(--mobile-nav-height))] bg-white z-10 overflow-y-auto space-y-6"
     
    >
      <div class="sticky top-0 z-10 bg-white px-4 py-2">
        <AppHeaderLogo
          :to="localePath({ name: 'index' })"
          size="sm"
          class="flex my-4 justify-center mx-auto w-fit"
          @click="handleCloseMoreMenu(localePath({ name: 'index' }))"
        />

        <MoleculePersona
          v-if="userExists"
          :avatar="{ text: userInitials, src: userAvatar }"
          :name="userFullName"
        >
          <template #sub-name>
            <NuxtLink :to="localePath({ name: 'profile-id', params: { id: userId } })">
              {{ t('viewProfile') }}
            </NuxtLink>
          </template>
        </MoleculePersona>
      </div>

      <div class="rounded-xl bg-white text-base/6 text-gray-900">
        <p class="font-semibold text-lg px-4">
          {{ t('hosting') }}
        </p>
        <div class="divide-y divide-gray-100 px-4">
          <TMenuItem
            :to="localePath({ name: 'dashboard-rvs', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.myListings')"
            icon="rv"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-rvs', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="localePath({ name: 'dashboard-insights', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.insights')"
            icon="chart-mixed-up-circle-dollar"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-insights', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="localePath({ name: 'dashboard-bookings-index', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.bookings')"
            icon="book-open"
            size="lg"
            show-right-chevron
           
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-bookings-index', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="localePath({ name: 'dashboard-rvs-new', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.createListing')"
            icon="rv"
            size="lg"
            show-right-chevron
           
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-rvs-new', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="localePath({ name: 'dashboard-profile-payout', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.payoutInfo')"
            icon="money-check-pen"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile-payout', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="t('helpCenter.url')"
            :text="t('helpCenter.title')"
            icon="message-question"
            size="lg"
            external
            show-right-chevron
            @click="handleTrackHelpCenterClicked"
          />

          <TMenuItem
            :text="t('pageLink.downloadApp')"
            icon="mobile"
            size="lg"
            show-right-chevron
            @click="downloadApp"
          />
        </div>

        <p class="font-semibold text-lg px-4 mt-6">
          {{ t('account') }}
        </p>
        <div class="divide-y divide-gray-100 px-4">
          <TMenuItem
            :to="localePath({ name: 'dashboard-profile', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.account')"
            icon="gear"
            size="lg"
            :show-badge="!userIsVerified || !userPhoneNumber"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="localePath({ name: 'dashboard-profile-notifications', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.notifications')"
            icon="bell"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile-notifications', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            v-if="userHasRv"
            :to="localePath({ name: 'index' })"
            :text="t('switchToTravelling')"
            icon="arrow-down-arrow-up"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'index' }))"
          />
        </div>

        <div class="divide-y divide-gray-100 px-4 mt-6">
          <TMenuItem
            :text="currentLocale?.name"
            icon="globe"
            size="lg"
            @click="toggleLocale"
          />

          <button
            type="button"
           
            class="text-base font-semibold underline text-primary-950 rounded-full py-2.5 transition-colors duration-300 ease-in-out hover:bg-gray-50 cursor-pointer"
            @click="logOut()"
          >
            {{ t('actions.logOut') }}
          </button>
        </div>
      </div>

      <div class="py-8" />
    </div>
  </AtomTransition>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'

const { t } = useI18n()
const localePath = useLocalePath()
const { dashboardMode } = useBaseName()
const {
  userAvatar,
  userHasRv,
  userIsVerified,
  userPhoneNumber,
  userId,
  userExists,
  userInitials,
  userFullName,
} = useUser()
const { logOut } = useAuthentication()
const { downloadApp } = useDownloadApp()
const { currentLocale, toggleLocale } = useAppLocalization()
const { dashboardNotificationsTotal, inboxNotificationsTotal, opportunitiesNotificationsTotal, isSuspended } = useNotificationsData()

const navItems = computed(() => {
  const items: { path?: string, icon: IconName, text: string, isActive?: boolean, onClick?: (e?: Event) => void, showBadge?: boolean, dataTestid?: string, exact?: boolean }[] = [
    {
      path: localePath({ name: 'dashboard', params: { dashboard: dashboardMode.value } }),
      icon: 'house',
      text: t('pageLink.home'),
      showBadge: dashboardNotificationsTotal.value > 0,
      exact: true,
    },

    {
      path: localePath({ name: 'dashboard-calendar', params: { dashboard: dashboardMode.value } }),
      icon: 'calendar',
      text: t('pageLink.calendar'),
    },

    {
      path: localePath({ name: 'dashboard-messages', params: { dashboard: dashboardMode.value } }),
      icon: 'comment',
      text: t('pageLink.inbox'),
      showBadge: inboxNotificationsTotal.value > 0,
      dataTestid: 'inbox',
    },

    {
      path: localePath({ name: 'dashboard-insights', params: { dashboard: dashboardMode.value } }),
      icon: 'chart-mixed-up-circle-dollar',
      text: t('pageLink.insights'),
      showBadge: isSuspended.value || opportunitiesNotificationsTotal.value > 0,
    },

    {
      icon: 'bars',
      text: t('pageLink.more'),
      isActive: moreMenuVisible.value,
      showBadge: userExists.value && (!userIsVerified.value || !userPhoneNumber.value),
      dataTestid: 'more-menu-toggle-mobile',
      onClick: toggleOpenMoreMenu,
    },
  ]

  return items
})

const moreMenuVisible = ref(false)

function toggleOpenMoreMenu() {
  moreMenuVisible.value = !moreMenuVisible.value
}

const route = useRoute()

function handleCloseMoreMenu(path?: string) {
  if (!path || route.path === path) {
    moreMenuVisible.value = false
  }
}

function isActive(path: string, exact: boolean) {
  if (moreMenuVisible.value) {
    return false
  }

  if (exact) {
    return route.path === path
  }

  return route.path.startsWith(path)
}

watch(route, () => {
  moreMenuVisible.value = false
}, {
  flush: 'post',
})

const { isMdBreakpoint } = useBreakpoint()
const { lock, unlock } = useDocumentScrollLock()
watch(
  [moreMenuVisible, isMdBreakpoint],
  ([newMoreMenuVisible, newIsMdBreakpoint]) => {
    if (newMoreMenuVisible && !newIsMdBreakpoint) {
      lock()
    }
    else {
      unlock()
    }
  },
  { immediate: true },
)

const { pageSource } = usePageSource()
function handleTrackHelpCenterClicked() {
  trackHelpCenterClicked({
    pageSource: pageSource.value,
  })
}
</script>

<i18n src="~~/i18n/locales/common/app.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "viewProfile": "View profile",
    "hosting": "Hosting",
    "account": "Account"
  },
  "fr": {
    "viewProfile": "Voir le profil",
    "hosting": "Mode hôte",
    "account": "Compte"
  }
}
</i18n>
